import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';

import type {PredictionJobComponent} from '../../prediction-jobs/PredictionJobTypes';
import {MODEL_CATEGORY, type ScientificModelNode} from '../ScientificModelMetadata';

export const PREDICTION_CONFIG_SCHEMA_VERSION_V1 = 1;

export interface AaSequenceIdInputComponent {
  _type: 'aaSequenceId';
  id: string;
}

export interface MoleculeIdInputComponent {
  _type: 'moleculeId';
  id: string;
}

export interface ProteinIdInputComponent {
  _type: 'proteinId';
  id: string;
}

export interface AminoAcidsInputComponent {
  _type: 'aminoAcids';
  name?: string;
  value: string;
}

export interface SmilesInputComponent {
  _type: 'smiles';
  name?: string;
  value: string;
}

export type IdPredictionInputComponent =
  | AaSequenceIdInputComponent
  | MoleculeIdInputComponent
  | ProteinIdInputComponent;

export interface FileIdInputComponent {
  _type: 'fileId';
  id: string;
  versionId?: string;
}

export type RawPredictionInputComponent = AminoAcidsInputComponent | SmilesInputComponent;

export type PredictionInputComponent =
  | IdPredictionInputComponent
  | RawPredictionInputComponent
  | FileIdInputComponent;

export function aaSequenceId(id: string): AaSequenceIdInputComponent {
  return {_type: 'aaSequenceId', id};
}

export function moleculeId(id: string): MoleculeIdInputComponent {
  return {_type: 'moleculeId', id};
}

export function proteinId(id: string): ProteinIdInputComponent {
  return {_type: 'proteinId', id};
}

export interface BulkPredictionItem {
  id: string;
  entityType: string;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function predictionInputComponentFromUnknown(component: unknown): PredictionInputComponent | null {
  if (component == null || typeof component !== 'object' || Array.isArray(component)) {
    return null;
  }
  const record = component as Record<string, unknown>;
  if (record._type === 'aaSequenceId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'moleculeId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'proteinId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'fileId') {
    const id = nonEmptyString(record.id);
    return id != null ? {_type: record._type, id} : null;
  }
  if (record._type === 'aminoAcids') {
    const value = nonEmptyString(record.value);
    const name = nonEmptyString(record.name);
    return value != null ? {_type: record._type, value, ...(name != null ? {name} : {})} : null;
  }
  if (record._type === 'smiles') {
    const value = nonEmptyString(record.value);
    const name = nonEmptyString(record.name);
    return value != null ? {_type: record._type, value, ...(name != null ? {name} : {})} : null;
  }
  return null;
}

function extractPredictionInputComponentsFromUnknown(components: unknown): Array<PredictionInputComponent> {
  if (Array.isArray(components)) {
    return components
      .map((component) => predictionInputComponentFromUnknown(component))
      .filter((component): component is PredictionInputComponent => component != null);
  }
  const component = predictionInputComponentFromUnknown(components);
  return component != null ? [component] : [];
}

export type PredictionInputComponentsField = 'component' | 'components' | 'targets';

export function getPredictionInputComponentsField(
  model: ScientificModelNode
): PredictionInputComponentsField {
  if (model.category === MODEL_CATEGORY.GENERATIVE) {
    return 'targets';
  }
  if (model.capabilities.supportsMultimers) {
    return 'components';
  }
  return 'component';
}

export function getAntibodyChainInputs(
  configJson: Record<string, unknown> | null | undefined
): Array<PredictionInputComponent> | null {
  const antibody = configJson?.antibody;
  if (antibody == null || typeof antibody !== 'object' || Array.isArray(antibody)) {
    return null;
  }
  const chains = antibody as Record<string, unknown>;
  const chainNames = [
    {key: 'heavy_chain', name: 'Heavy chain'},
    {key: 'light_chain', name: 'Light chain'},
  ];
  return chainNames.flatMap(({key, name}): Array<PredictionInputComponent> => {
    const component = predictionInputComponentFromUnknown(chains[key]);
    if (component == null) {
      return [];
    }
    return [isRawPredictionInputComponent(component) ? {...component, name} : component];
  });
}

export function extractPredictionInputComponents(
  configJson: Record<string, unknown>,
  model: ScientificModelNode
): Array<PredictionInputComponent> {
  const field = getPredictionInputComponentsField(model);
  return extractPredictionInputComponentsFromUnknown(configJson[field]);
}

export function predictionInputComponentFromEntityId(entityId: string): IdPredictionInputComponent {
  if (APIUtil.isMolecule(entityId)) {
    return moleculeId(entityId);
  }
  // APIUtil.isProtein refers to legacy AA sequences with prtn_ IDs.
  if (APIUtil.isProtein(entityId)) {
    return aaSequenceId(entityId);
  }
  // New Protein inputs use the generic basic folder item bfi_ prefix.
  if (APIUtil.isBasicFolderItem(entityId)) {
    return proteinId(entityId);
  }
  throw new Error(`Unsupported prediction input component entity ID: ${entityId}`);
}

function resolveConfiguredInputComponents(
  components: ReadonlyArray<PredictionJobComponent>,
  configJson: Record<string, unknown> | null | undefined,
  model: ScientificModelNode
): Array<PredictionInputComponent> {
  if (configJson != null) {
    const antibodyChains = getAntibodyChainInputs(configJson);
    if (antibodyChains != null && antibodyChains.length > 0) {
      return antibodyChains;
    }
    const fromConfig = extractPredictionInputComponents(configJson, model);
    if (fromConfig.length > 0) {
      return fromConfig;
    }
    // Models with a single file input store it under `target`.
    const target = predictionInputComponentFromUnknown(configJson.target);
    if (target != null) {
      return [target];
    }
  }
  return components.map<PredictionInputComponent>(({id}) =>
    APIUtil.isFile(id) ? {_type: 'fileId', id} : predictionInputComponentFromEntityId(id)
  );
}

/** Resolves a prediction job's inputs, giving each file input the version the job ran on. */
export function resolvePredictionInputComponents(
  components: ReadonlyArray<PredictionJobComponent>,
  configJson: Record<string, unknown> | null | undefined,
  model: ScientificModelNode
): Array<PredictionInputComponent> {
  const versionIdsById = getVersionIdsById(components);
  return resolveConfiguredInputComponents(components, configJson, model).map((component) => {
    const versionId = isFileIdInputComponent(component) ? versionIdsById.get(component.id) : undefined;
    return versionId != null ? {...component, versionId} : component;
  });
}

/** A target structure given as the text of a PDB or mmCIF file rather than as a Benchling File. */
export interface InlineStructureTarget {
  text: string;
  format: 'pdb' | 'cif';
}

/** Reads a target stored as structure text, which is not one of the job's components. */
export function getInlineStructureTarget(
  configJson: Record<string, unknown> | null | undefined
): InlineStructureTarget | null {
  const text = nonEmptyString(configJson?.target);
  if (text == null) {
    return null;
  }
  // The config defaults `inputType` to PDB when it is omitted.
  return {text, format: configJson?.inputType === 'cif' ? 'cif' : 'pdb'};
}

/** Maps each input that has a recorded version to the ID of that version. */
export function getVersionIdsById(components: ReadonlyArray<PredictionJobComponent>): Map<string, string> {
  return new Map(components.flatMap(({id, versionId}) => (versionId != null ? [[id, versionId]] : [])));
}

export function isAminoAcidsInputComponent(
  component: PredictionInputComponent
): component is AminoAcidsInputComponent {
  return component._type === 'aminoAcids';
}

export function isSmilesInputComponent(
  component: PredictionInputComponent
): component is SmilesInputComponent {
  return component._type === 'smiles';
}

export function isAaSequenceIdInputComponent(
  component: PredictionInputComponent
): component is AaSequenceIdInputComponent {
  return component._type === 'aaSequenceId';
}

export function isMoleculeIdInputComponent(
  component: PredictionInputComponent
): component is MoleculeIdInputComponent {
  return component._type === 'moleculeId';
}

export function isProteinIdInputComponent(
  component: PredictionInputComponent
): component is ProteinIdInputComponent {
  return component._type === 'proteinId';
}

export function isFileIdInputComponent(
  component: PredictionInputComponent
): component is FileIdInputComponent {
  return component._type === 'fileId';
}

export function isRawPredictionInputComponent(
  component: PredictionInputComponent
): component is RawPredictionInputComponent {
  return isAminoAcidsInputComponent(component) || isSmilesInputComponent(component);
}

export function isIdPredictionInputComponent(
  component: PredictionInputComponent
): component is IdPredictionInputComponent {
  return (
    isAaSequenceIdInputComponent(component) ||
    isMoleculeIdInputComponent(component) ||
    isProteinIdInputComponent(component)
  );
}

export function getRawModelInputChipDisplayLabel(component: RawPredictionInputComponent): string {
  return component.name ?? (isAminoAcidsInputComponent(component) ? 'AA Sequence' : 'Molecule');
}

const INPUT_TYPE_TO_FOLDER_ITEM_TYPE: Record<string, OligoAwareFolderItemType> = {
  amino_acid_sequence: ModelTypeConstants.PROTEIN,
  molecule: ModelTypeConstants.MOLECULE,
};

export function acceptsStandaloneMolecule(model: ScientificModelNode): boolean {
  return model.inputTypes.includes('molecule') && !model.capabilities.supportsMultimers;
}

export function getAvailableTypesForModel(
  inputTypes: Array<string>,
  {includeMolecules = false}: {includeMolecules?: boolean} = {}
): Array<OligoAwareFolderItemType> {
  return inputTypes
    .map((t) => INPUT_TYPE_TO_FOLDER_ITEM_TYPE[t])
    .filter((t): t is OligoAwareFolderItemType => t != null)
    .filter((t) => includeMolecules || t !== ModelTypeConstants.MOLECULE);
}

export function isEntityTypeCompatible(
  entityType: string,
  inputTypes: Array<string>,
  options: {includeMolecules?: boolean} = {}
): boolean {
  const compatibleFolderItemTypes = getAvailableTypesForModel(inputTypes, options);
  return compatibleFolderItemTypes.includes(entityType as OligoAwareFolderItemType);
}

export type PredictionConfig = Record<string, unknown>;

type PredictionConfigBuilder = (item: BulkPredictionItem) => PredictionConfig;

interface PredictionJobConfig {
  config: PredictionConfig;
  name?: string | null;
}

const singleComponentConfigBuilder: PredictionConfigBuilder = (item) => ({
  component: predictionInputComponentFromEntityId(item.id),
});
const boltzGenConfigBuilder: PredictionConfigBuilder = (item) => ({
  targets: [predictionInputComponentFromEntityId(item.id)],
});
const multimerListConfigBuilder: PredictionConfigBuilder = (item) => ({
  components: [predictionInputComponentFromEntityId(item.id)],
});

function getPredictionConfigBuilder(model: ScientificModelNode): PredictionConfigBuilder {
  const field = getPredictionInputComponentsField(model);
  if (field === 'targets') {
    return boltzGenConfigBuilder;
  }
  if (field === 'components') {
    return multimerListConfigBuilder;
  }
  return singleComponentConfigBuilder;
}

export function buildPredictionConfigs(
  model: ScientificModelNode,
  items: Array<BulkPredictionItem>
): Array<PredictionConfig> {
  const builder = getPredictionConfigBuilder(model);
  return items.map((item) => builder(item));
}

export function buildPredictionBatchName(model: ScientificModelNode): string {
  return `${model.displayName} run (1.0)`;
}

export async function generatePredictionBatchName(model: ScientificModelNode): Promise<string> {
  const response: {defaultName: string} = await net.postJSONPromise({
    url: '/1/api/model-hub/prediction-batch:generate-name',
    data: {model: model.externalReference},
  });
  return response.defaultName;
}

export interface BulkPredictionSubmitParams {
  folderId?: string;
  model: ScientificModelNode;
  // Optional: the New prediction flow may omit this so the backend generates a default batch name
  // (via /model-hub/prediction-batch:generate-name).
  name?: string;
  items?: Array<BulkPredictionItem>;
  predictionConfigs?: Array<PredictionConfig>;
  // Optional per-job names, positionally aligned with predictionConfigs/items.
  jobNames?: Array<string | null>;
  modelParams?: Record<string, unknown>;
  source?: PredictionBatchSource;
}

export async function submitBulkPrediction(
  params: BulkPredictionSubmitParams
): Promise<{predictionBatchId: string}> {
  const {folderId, model, items, modelParams, source, name, jobNames} = params;
  if (params.predictionConfigs == null && items == null) {
    throw new Error('Must provide items or prediction configs');
  }
  if (params.predictionConfigs != null && items != null) {
    throw new Error('Must provide only one of items or prediction configs');
  }
  const predictionConfigs = params.predictionConfigs ?? buildPredictionConfigs(model, items ?? []);

  const jobConfigs: Array<PredictionJobConfig> = predictionConfigs.map((predictionConfig, index) => {
    const jobName = jobNames?.[index];
    return {
      config: {
        ...predictionConfig,
        ...(modelParams === undefined ? {} : modelParams),
      },
      ...(jobName != null && jobName !== '' ? {name: jobName} : {}),
    };
  });

  const data: Record<string, unknown> = {
    model: model.externalReference,
    jobConfigs,
    configSchemaVersion: PREDICTION_CONFIG_SCHEMA_VERSION_V1,
    source,
    name,
  };
  if (folderId != null) {
    data.folderId = folderId;
  }

  return await net.postJSONPromise({
    url: '/1/api/model-hub/prediction-batch',
    data,
  });
}
